// SuggestImageStylesToolUI — branded tool-UI for the agent's `suggestImageStyles` tool
// call, rendered inline in the Aidea Assistant chat thread.
//
// The backend returns a JSON string:
//   { "concept": "...", "prompt": "...enhanced prompt...", "aspectRatio": "16:9",
//     "styles": [ { "id": 12, "name": "Isometric", "sampleImageUrl": "https://...", "why": "clean technical look" }, ... ] }
//
// Each style tile is a clickable button that dispatches dispatchOpenAIPopup with
// the style's id, name, and the shared aspectRatio. After click, the tile shows
// a subtle "Opening..." hint via local state.
//
// Styling: every utility is `tw-`-prefixed and takes effect under `.aidea-chat-root`.
// Brand tokens via CSS vars. No emoji.

import React, { useState } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, ImageGenIcon, AlertIcon } from "../icons";
import { dispatchOpenAIPopup } from "../aiPopupGateway";

// Parse the tool result defensively (string or object; never throw).
function parseStylesResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  const concept = typeof data.concept === "string" ? data.concept.trim() : "";
  const prompt = typeof data.prompt === "string" ? data.prompt.trim() : "";
  const aspectRatio = typeof data.aspectRatio === "string" ? data.aspectRatio.trim() : "16:9";
  const styles = Array.isArray(data.styles) ? data.styles : [];
  return { concept, prompt, aspectRatio, styles };
}

// Small violet-tint header chip.
function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <ImageGenIcon size={12} />
      {label}
    </span>
  );
}

// Card chrome shared by every state.
function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[520px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

// Running state: spinner + "Finding style options..."
function RunningState() {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Finding style options&hellip;
        </span>
      </div>
    </Card>
  );
}

// Fallback thumbnail when sampleImageUrl is absent.
function ThumbnailFallback() {
  return (
    <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
      <ImageGenIcon size={22} />
    </div>
  );
}

// A single clickable style tile.
function StyleTile({ style, prompt, aspectRatio, onSelect, selected }) {
  const handleClick = () => {
    onSelect(style.id);
    dispatchOpenAIPopup({
      feature: "image",
      prompt,
      settings: { styleId: style.id, style: style.name, aspectRatio },
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        "tw-flex tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-text-left tw-transition-all",
        "tw-cursor-pointer tw-p-0",
        selected
          ? "tw-border-[var(--aidea-primary)] tw-ring-1 tw-ring-[var(--aidea-primary)]"
          : "tw-border-[var(--aidea-border)] hover:tw-border-[var(--aidea-primary)]",
      ].join(" ")}
      style={{ background: "var(--aidea-surface)" }}
    >
      {/* Thumbnail */}
      <div className="tw-relative tw-h-[80px] tw-w-full tw-overflow-hidden tw-bg-[var(--aidea-surface-alt,#f4f4f6)]">
        {style.sampleImageUrl ? (
          <img
            src={style.sampleImageUrl}
            alt={style.name}
            loading="lazy"
            className="tw-h-full tw-w-full tw-object-cover"
          />
        ) : (
          <ThumbnailFallback />
        )}
      </div>
      {/* Labels */}
      <div className="tw-px-2 tw-py-1.5">
        <p className="tw-m-0 tw-text-[12px] tw-font-semibold tw-leading-snug tw-text-[var(--aidea-fg)]">
          {style.name}
        </p>
        {style.why && (
          <p className="tw-m-0 tw-mt-0.5 tw-text-[11px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
            {style.why}
          </p>
        )}
        {selected && (
          <p className="tw-m-0 tw-mt-1 tw-text-[11px] tw-font-medium tw-text-[var(--aidea-primary)]">
            Opening&hellip;
          </p>
        )}
      </div>
    </button>
  );
}

// Empty state when the backend returned no styles.
function EmptyState({ concept }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Image styles" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">
          {concept
            ? `No style options for "${concept}" — describe the look you want and I'll open the generator.`
            : "No style options available — describe the look you want and I'll open the generator."}
        </span>
      </div>
    </Card>
  );
}

// Complete state: header chip + concept line + up to 3 style tiles.
function CompleteState({ concept, prompt, aspectRatio, styles }) {
  const [selectedId, setSelectedId] = useState(null);

  if (!styles.length) {
    return <EmptyState concept={concept} />;
  }

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Image styles" />
      </div>
      {concept && (
        <p className="tw-m-0 tw-px-3 tw-pt-1.5 tw-text-[12px] tw-text-[var(--aidea-muted-fg)]">
          Pick a direction for:{" "}
          <span className="tw-font-medium tw-text-[var(--aidea-fg)]">{concept}</span>
        </p>
      )}
      <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-px-3 tw-pb-3 tw-pt-2">
        {styles.slice(0, 3).map((style) => (
          <StyleTile
            key={style.id}
            style={style}
            prompt={prompt}
            aspectRatio={aspectRatio}
            onSelect={setSelectedId}
            selected={selectedId === style.id}
          />
        ))}
      </div>
    </Card>
  );
}

export const SuggestImageStylesToolUI = makeAssistantToolUI({
  toolName: "suggestImageStyles",
  render: ({ args, result, status }) => {
    if (status && status.type === "running") {
      return <RunningState />;
    }

    const parsed = parseStylesResult(result);
    if (!parsed) {
      return (
        <Card>
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
            <HeaderChip label="Image styles" />
          </div>
          <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
            <AlertIcon
              size={15}
              className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]"
            />
            <span className="tw-min-w-0">Couldn't load style options.</span>
          </div>
        </Card>
      );
    }

    return (
      <CompleteState
        concept={parsed.concept}
        prompt={parsed.prompt}
        aspectRatio={parsed.aspectRatio}
        styles={parsed.styles}
      />
    );
  },
});

export default SuggestImageStylesToolUI;
